import React, { useEffect, useRef, useState } from 'react';
import { Box, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, RootRef, useMediaQuery, useTheme } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { HoverableBox } from '../Containers';
import { ToggleableChevron } from '../ToggleableChevron';
import { Typography, TypographyProps } from '../Typography';

interface SelectProps<T> {
    handleMenuItemClick: (id: T) => void;
    isDisabled?: boolean;
    selectedId: T | null;
    menuItems: Array<{ id: T; name: string}>;
    selectedName?: string;
    label?: string;
    reduceFontOnMobile?: boolean;
    dropdownPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
}

const Select = <T, >({
    selectedId,
    menuItems,
    handleMenuItemClick,
    isDisabled,
    selectedName,
    dropdownPlacement,
    label,
    reduceFontOnMobile,
}: SelectProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    let selectedNameDefaultVariant: undefined | TypographyProps['variant'];

    if (!isDesktop && reduceFontOnMobile) {
        selectedNameDefaultVariant = 'body2';
    }
    const onHandleMenuItemClick = (id: T) => {
        setIsOpen(false);
        handleMenuItemClick(id);
    };

    const anchorRef = useRef<HTMLButtonElement>(null);

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = useRef(isOpen);
    useEffect(() => {
        if (anchorRef.current && prevOpen.current === true && isOpen === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = isOpen;
    }, [isOpen]);

    return (
        <>
            <RootRef rootRef={anchorRef}>
                <Box display="flex" flexDirection="column">
                    {label && (
                        <>
                            <Typography variant="caption">{label}</Typography>
                            <Box mb={0.5} />
                        </>
                    )}
                    <HoverableBox
                        aria-controls={isOpen ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={() => setIsOpen(true)}
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                    >
                        <Typography variant={label ? 'subtitle1' : selectedNameDefaultVariant}>{selectedName}</Typography>
                        <Box marginRight={1} />
                        <ToggleableChevron open={isOpen} />
                    </HoverableBox>
                </Box>
            </RootRef>
            <Popper open={isOpen} anchorEl={anchorRef.current} role={undefined} transition disablePortal placement={dropdownPlacement}>
                {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                                <MenuList autoFocusItem={isOpen} id="menu-list-grow">
                                    {menuItems.map(({ id, name }, index) => (
                                        <MenuItem
                                            key={id as unknown as string || index}
                                            selected={id === selectedId}
                                            disabled={isDisabled}
                                            onClick={() => onHandleMenuItemClick(id)}
                                        >
                                            <Box display='flex' alignItems='center' justifyContent='space-between' minWidth='320px'>
                                                {name}
                                                {id === selectedId ?
                                                    (
                                                        <Box display='flex' color="#39CB85">
                                                            <CheckCircleIcon />
                                                        </Box>
                                                    ) : null}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default Select;
